import { useMemo } from 'react';
import { ProductHelper } from '@models/graphql';
import type { EchainPartsListItem } from '@hooks/states/echainPartsListSlice';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { UnitCode, UnitSystem } from '@icommerce/units';
import type { ProductAttributeValueAmountUnit, ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue, getAttributeValueAmount, getAttributeValueAmountLabel } from '@libs/attribute.helper';
import type { WidenMediaElement } from '@libs/widen-utils';
import { MediaElementType } from '@libs/widen-utils';
import type {
  ModelAssociationFragmentEnhanced,
  ModelAssociationProductFragmentEnhanced,
} from '@models/enhanced-fragments';

export const SHELVE_ITEM_MAX_DISPLAY = 4;

export type SortedProductWidthValues = { id: string; unitCode: UnitCode; width: number; label: string };

export type ShelveItem = {
  key: number;
  associatedModel: ModelAssociationFragmentEnhanced;
  selectedAssociationProduct?: ModelAssociationProductFragmentEnhanced;
  sortedProductWidthValues: SortedProductWidthValues[];
  partsListItem?: EchainPartsListItem;
  teaserImage?: WidenMediaElement;
};

const getSortedProductWidthValues = (products: ModelAssociationProductFragmentEnhanced[], unitSystem: UnitSystem) =>
  products
    .sort((a, b) => {
      const aWidth = getAttributeValue<ProductAttributeValueAmountUnit>(
        a.attributes?.width as ProductAttribute<ProductAttributeValueAmountUnit>
      )?.amount;
      const bWidth = getAttributeValue<ProductAttributeValueAmountUnit>(
        b.attributes?.width as ProductAttribute<ProductAttributeValueAmountUnit>
      )?.amount;
      return aWidth - bWidth;
    })
    .map(product => ({
      id: product.id,
      unitCode: getAttributeValue<ProductAttributeValueAmountUnit>(
        product.attributes?.width as ProductAttribute<ProductAttributeValueAmountUnit>
      )?.unit,
      width: getAttributeValueAmount(
        getAttributeValue<ProductAttributeValueAmountUnit>(
          product.attributes?.width as ProductAttribute<ProductAttributeValueAmountUnit>
        ),
        unitSystem
      ),
      label: getAttributeValueAmountLabel(
        getAttributeValue<ProductAttributeValueAmountUnit>(
          product.attributes?.width as ProductAttribute<ProductAttributeValueAmountUnit>
        ),
        unitSystem
      ),
    }));

export const useShelveItems = (
  shelvesAssociationModels: ModelAssociationFragmentEnhanced[],
  selectedAssociationProductPerModel: Record<string, ModelAssociationProductFragmentEnhanced>,
  showMoreShelves: boolean,
  standardShelveModelKey: string
): ShelveItem[] => {
  const listLength = shelvesAssociationModels?.length || 0;
  const maxLength = showMoreShelves ? listLength : SHELVE_ITEM_MAX_DISPLAY;
  const unitSystem = useProductPageStore(state => state.unitSystem);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const eChainPartsList = useEchainProductStore(state => state.eChainPartsList);
  const getEchainPartsListItem = useEchainProductStore(state => state.getEchainPartsListItem);

  return useMemo<ShelveItem[]>(() => {
    const validModels = shelvesAssociationModels
      .map(model => {
        if (!Array.isArray(model.products)) {
          return { ...model, products: [] };
        }

        // Keep only products that have all required fields
        const filteredProducts = model.products.filter(
          product =>
            !!product?.attributes?.series_description &&
            !!product?.attributes?.width &&
            Array.isArray(product.assets) &&
            product.assets.length > 0
        );

        return { ...model, products: filteredProducts };
      })
      // Keep only models that have at least one valid product
      .filter(model => model.products.length > 0)
      .filter((_, index) => index < maxLength)
      .sort((a, b) => {
        if (a.id === b.id) {
          return 0;
        }
        if (a.id === standardShelveModelKey) {
          return -1;
        }
        if (b.id === standardShelveModelKey) {
          return 1;
        }
        return 0;
      });

    return validModels.map((model, index) => {
      const sortedProductWidthValues = getSortedProductWidthValues(model.products, unitSystem);

      const selectedAssociationProduct =
        selectedAssociationProductPerModel?.[model.id] ??
        model.products.find(p => p.id === sortedProductWidthValues[0]?.id);

      const partsListItem =
        currentProduct && selectedAssociationProduct
          ? getEchainPartsListItem(selectedAssociationProduct.id)
          : undefined;

      const teaserImage = selectedAssociationProduct
        ? ProductHelper.getAssetsMedia(selectedAssociationProduct).productMedia.find(
            media => media.type === MediaElementType.DRAWING
          )
        : undefined;

      return {
        key: index,
        associatedModel: model,
        selectedAssociationProduct,
        sortedProductWidthValues,
        partsListItem,
        teaserImage,
      };
    });

    // eChainPartsList is required as dependency because getEchainPartsListItem depends on it indirectly and so that the parts list is fetched and rendered correctly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentProduct,
    getEchainPartsListItem,
    maxLength,
    shelvesAssociationModels,
    selectedAssociationProductPerModel,
    standardShelveModelKey,
    eChainPartsList,
  ]);
};
